
import { useState, useEffect } from 'react';
import { useInteractiveTutorial } from './useInteractiveTutorial';
import { useSettings } from '@/context/SettingsContext';

const ONBOARDING_KEY = 'unstuck_onboarding_completed';
const TUTORIAL_COMPLETED_KEY = 'unstuck_tutorial_completed';

export const useOnboarding = () => {
  const { tutorialCompleted, setTutorialCompleted, saveSettings } = useSettings();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { startTutorial } = useInteractiveTutorial();

  // Check localStorage for legacy tutorial completion
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState<boolean>(() => {
    try {
      return localStorage.getItem(ONBOARDING_KEY) === 'true' || 
             localStorage.getItem(TUTORIAL_COMPLETED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Use settings context tutorial completion status primarily
  const isOnboardingCompleted = tutorialCompleted || hasCompletedOnboarding;

  useEffect(() => {
    // Show interactive tutorial for new users after a short delay
    if (!isOnboardingCompleted) {
      const timer = setTimeout(() => {
        setShowOnboarding(true);
        startTutorial();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isOnboardingCompleted, startTutorial]);

  const completeOnboarding = async () => {
    try {
      // Update settings context (which handles both localStorage and database)
      setTutorialCompleted(true);
      await saveSettings();
      
      // Also update legacy localStorage keys for backward compatibility
      localStorage.setItem(ONBOARDING_KEY, 'true');
      localStorage.setItem(TUTORIAL_COMPLETED_KEY, 'true');
      
      setHasCompletedOnboarding(true);
      setShowOnboarding(false);
    } catch (error) {
      console.warn('Failed to save onboarding completion:', error);
    }
  };

  const skipOnboarding = () => {
    completeOnboarding();
  };

  const resetOnboarding = async () => {
    try {
      // Clear settings context
      setTutorialCompleted(false);
      await saveSettings();
      
      // Clear localStorage
      localStorage.removeItem(ONBOARDING_KEY);
      localStorage.removeItem(TUTORIAL_COMPLETED_KEY);
      localStorage.removeItem('unstuck_tutorial_state');
      
      setHasCompletedOnboarding(false);
    } catch (error) {
      console.warn('Failed to reset onboarding:', error);
    }
  };

  const restartTutorial = async () => {
    try {
      // Clear all tutorial and onboarding completion states
      localStorage.removeItem('unstuck_tutorial_state');
      localStorage.removeItem(ONBOARDING_KEY);
      localStorage.removeItem(TUTORIAL_COMPLETED_KEY);
      
      // Reset in settings context and database
      setTutorialCompleted(false);
      await saveSettings();
      
      // Reset the onboarding state so tutorial can trigger
      setHasCompletedOnboarding(false);
      
      // DON'T immediately start tutorial or navigate - let user choose when
      console.log("Tutorial state reset - ready for user to start when they navigate to appropriate page");
    } catch (error) {
      console.warn('Failed to restart tutorial:', error);
      throw error; // Re-throw so UI can handle the error
    }
  };

  return {
    hasCompletedOnboarding: isOnboardingCompleted,
    showOnboarding,
    completeOnboarding,
    skipOnboarding,
    resetOnboarding,
    restartTutorial
  };
};
